import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {DeviceProperty} from "../../../../model/device-property";
import {Tenant} from "../../../../model/tenant";
import {FormArray, FormBuilder, FormGroup, Validators} from "@angular/forms";
import {PropertyType} from "../../../../model/property-type";
import {ActionButton} from "../../../../model/ui/action-button";
import {AssetProperty} from "../../../../model/asset-property";
import {TenantsService} from "../../../../services/tenants.service";
import {TemplatesService} from "../../../../services/templates.service";
import {PreviousRouteService} from "../../../../services/previous-route.service";
import {noSpecialCharacters} from "../../../../shared/custom-validators";
import {translate} from "@ngneat/transloco";
import {Template} from "../../../../model/template";

@Component({
  selector: 'app-templates-form-properties',
  templateUrl: './templates-form-properties.component.html',
  styleUrls: ['./templates-form-properties.component.scss']
})
export class TemplatesFormPropertiesComponent implements OnInit {

  @Input() mode: 'create' | 'edit';

  @Input() type: string;
  @Input() deviceProperties: DeviceProperty[];
  @Input() assetProperties: AssetProperty[];
  @Output() devicePropertiesChange = new EventEmitter<DeviceProperty[]>();
  @Output() assetPropertiesChange = new EventEmitter<AssetProperty[]>();
  @Output() previousStep = new EventEmitter<void>();
  @Output() nextStep = new EventEmitter<void>();
  @Output() cancel = new EventEmitter<string>();

  @Input() tenant: Tenant;
  tenantIsEmpty: boolean;

  templateProperties: (DeviceProperty | AssetProperty)[];
  templatePropertiesForm: FormGroup;
  rowsForm: FormArray;

  propertyTypes: PropertyType[];
  clonedProperties: { [s: string]: DeviceProperty; } = {};

  displayCopyFromTemplate: boolean;
  copyFromTemplateActions: ActionButton[];

  constructor(private tenantsService: TenantsService,
              private templatesService: TemplatesService,
              private formBuilder: FormBuilder,
              private previousService: PreviousRouteService) {
  }

  ngOnInit(): void {
    this.templateProperties = (this.type === 'DEVICE') ? this.deviceProperties : this.assetProperties;
    this.buildCopyFromTemplateActions();
    if (!this.tenant) {
      this.tenantIsEmpty = true;
    } else {
      this.loadAndCreateForm();
    }
  }

  private loadAndCreateForm(): void {
    this.tenantsService.getTenantPropertyTypes(this.tenant.uuid).toPromise().then(response => {
      this.propertyTypes = response.propertyTypes
        .sort((a, b) => a.name > b.name ? 1 : -1);
      this.createForm(this.templateProperties);
    });
  }

  private createForm(templateProperties: (DeviceProperty | AssetProperty)[]): void {
    this.rowsForm = this.formBuilder.array(
      templateProperties.map(property => this.formGroupFromProperty(property))
    );
    this.templatePropertiesForm = this.formBuilder.group({
      templateProperties: this.rowsForm
    });
  }

  addProperty(): void {
    const newProperty = this.formGroupFromProperty((this.type === 'DEVICE') ? new DeviceProperty() : new AssetProperty());
    this.rowsForm.push(newProperty);
  }

  onRowDelete(rowIndex: number): void {
    this.rowsForm.removeAt(rowIndex);
  }

  deleteAllRows(): void {
    while (this.rowsForm.length > 0) {
      this.rowsForm.removeAt(0);
    }
  }

  onPreviousStep(): void {
    const properties = this.templatePropertiesForm.get('templateProperties').value;
    this.previousStep.emit();
    if (this.type === 'DEVICE') {
      this.devicePropertiesChange.emit(properties);
    } else {
      this.assetPropertiesChange.emit(properties);
    }
  }

  onNextStep(): void {
    if (this.templatePropertiesForm.valid) {
      const properties = this.templatePropertiesForm.get('templateProperties').value;
      this.nextStep.emit();
      if (this.type === 'DEVICE') {
        this.devicePropertiesChange.emit(properties);
      } else {
        this.assetPropertiesChange.emit(properties);
      }
    }
  }

  private formGroupFromProperty(property: DeviceProperty | AssetProperty): FormGroup {
    return this.formBuilder.group({
      name: [property.name, [
        Validators.maxLength(255),
        Validators.required,
        noSpecialCharacters
      ]],
      value: [property.value],
      propertyType: [property.propertyType, [Validators.required]],
      uuid: [property.uuid]
    });
  }

  onCancel(): void {
    this.cancel.emit(this.previousService.getPreviousUrl());
  }

  private buildCopyFromTemplateActions(): void {
    this.copyFromTemplateActions = [
      {
        label: translate('misc.copyProperties'),
        icon: 'pi pi-copy',
        action: (template: Template) => this.copyProperties(template)
      }, {
        label: translate('misc.appendProperties'),
        icon: 'pi pi-plus',
        action: (template: Template) => this.copyProperties(template, true)
      }
    ];
  }

  copyProperties(template: Template, append: boolean = false): void {
    const measurements = this.templatePropertiesForm.get('templateProperties').value;
    this.templatesService.getTemplate(template.uuid).toPromise().then(response => {
      if (!append) {
        this.deleteAllRows();
      }
      let fullTemplate = response.template;
      if (fullTemplate.type === 'DEVICE' && fullTemplate.content?.deviceProperties) {
        fullTemplate.content.deviceProperties.forEach((property: DeviceProperty) => {
          property.uuid = null;
          if (measurements.find(sensor => sensor.name === property.name) === undefined) {
            this.rowsForm.push(this.formGroupFromProperty(property));
          }
        });
      } else if (fullTemplate.type === 'ASSET' && fullTemplate.content?.assetProperties) {
        fullTemplate.content.assetProperties.forEach((property: AssetProperty) => {
          property.uuid = null;
          if (measurements.find(sensor => sensor.name === property.name) === undefined) {
            this.rowsForm.push(this.formGroupFromProperty(property));
          }
        });
      }
      this.displayCopyFromTemplate = false;
    });
  }

}
